import i18n from 'i18next'
import { sleep } from '@/pages/utils/sleep'

const COMMAND_WAIT_MS = 500
const HELP_REPLY_TIMEOUT_MS = 5000
const HELP_REPLY_POLL_MS = 50

type CreateSessionInput = {
  activePort: SerialPortConnection
  onTerminalLog: OnTerminalLog
  onSerialOutput: OnSerialOutput
}

export type OnTerminalLog = (message: string) => void
export type OnSerialOutput = (chunk: string) => void

export type SerialPortInfo = {
  usbVendorId?: number
  usbProductId?: number
}

export type SerialPortConnection = {
  open: (options: { baudRate: number }) => Promise<void>
  close: () => Promise<void>
  getInfo?: () => SerialPortInfo
  readable: ReadableStream<Uint8Array> | null
  writable: WritableStream<Uint8Array> | null
}

export type Serial = {
  sendCommand: (command: string) => Promise<string>
  readUntilOutput: () => Promise<string>
  writeRaw: (payload: Uint8Array | string) => Promise<void>
  closeSession: () => Promise<void>
}

export type SerialNavigator = Navigator & {
  serial?: {
    getPorts: () => Promise<SerialPortConnection[]>
  }
}

const ensurePortStreamsAvailable = (
  activePort: SerialPortConnection,
): {
  readable: ReadableStream<Uint8Array>
  writable: WritableStream<Uint8Array>
} => {
  if (!activePort.readable || !activePort.writable) {
    throw new Error(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.errors.usbStreamUnavailable',
      ),
    )
  }

  return {
    readable: activePort.readable,
    writable: activePort.writable,
  }
}

const createOutputCursor = () => {
  let serialOutput = ''
  let cursor = 0

  return {
    append: (chunk: string) => {
      serialOutput += chunk
    },
    readNewOutput: (): string => {
      const output = serialOutput.slice(cursor)
      cursor = serialOutput.length
      return output
    },
  }
}

const startReadLoop = ({
  reader,
  decoder,
  appendOutput,
  onSerialOutput,
}: {
  reader: ReadableStreamDefaultReader<Uint8Array>
  decoder: TextDecoder
  appendOutput: (chunk: string) => void
  onSerialOutput: OnSerialOutput
}) =>
  (async () => {
    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        if (!value) continue

        const chunk = decoder.decode(value, { stream: true })
        appendOutput(chunk)
        onSerialOutput(chunk)
      }
    } catch {
      // Reader cancellation during cleanup is expected.
    }
  })()

const closeSessionResources = async ({
  writer,
  reader,
  readLoop,
}: {
  writer: WritableStreamDefaultWriter<Uint8Array>
  reader: ReadableStreamDefaultReader<Uint8Array>
  readLoop: Promise<void>
}) => {
  try {
    await writer.close()
  } catch {
    // Port can drop during mode switching.
  }

  try {
    await reader.cancel()
  } catch {
    // Port can drop during mode switching.
  }

  await readLoop
  reader.releaseLock()
}

export const closeSessionAndPort = async (
  session: Serial,
  port: SerialPortConnection,
): Promise<void> => {
  await session.closeSession()
  try {
    await port.close()
  } catch {
    // Port may already be closed or dropped.
  }
}

export const createSerialSession = ({
  activePort,
  onTerminalLog,
  onSerialOutput,
}: CreateSessionInput): Serial => {
  const { readable, writable } = ensurePortStreamsAvailable(activePort)

  const encoder = new TextEncoder()
  const decoder = new TextDecoder()
  const writer = writable.getWriter()
  const reader = readable.getReader()
  const outputCursor = createOutputCursor()
  const readLoop = startReadLoop({
    reader,
    decoder,
    appendOutput: outputCursor.append,
    onSerialOutput,
  })
  let sessionClosed = false

  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.serialSessionStarted',
    ),
  )

  const sendCommand = async (command: string): Promise<string> => {
    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.sendingCommand',
        { command },
      ),
    )
    await writer.write(encoder.encode(`${command}\r\n`))
    await sleep(COMMAND_WAIT_MS)
    return outputCursor.readNewOutput()
  }

  const readUntilOutput = async (): Promise<string> => {
    const startTime = Date.now()
    let output = ''

    while (Date.now() - startTime < HELP_REPLY_TIMEOUT_MS) {
      output += outputCursor.readNewOutput()
      if (output.length > 0) {
        return output
      }
      await sleep(HELP_REPLY_POLL_MS)
    }

    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.noSerialOutputBeforeTimeout',
      ),
    )
    return output
  }

  const writeRaw = async (payload: Uint8Array | string) => {
    await writer.write(
      typeof payload === 'string' ? encoder.encode(payload) : payload,
    )
  }

  const closeSession = async () => {
    if (sessionClosed) {
      return
    }

    sessionClosed = true
    await closeSessionResources({ writer, reader, readLoop })
    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.serialSessionClosed',
      ),
    )
  }

  return {
    sendCommand,
    readUntilOutput,
    writeRaw,
    closeSession,
  }
}
